import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiChevronDown, FiEdit3, FiHelpCircle, FiTrash2 } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import { getCoveringApproverRules } from "../../utils/poApprovalCoverage";
import { parStockCoverageLabel } from "../../utils/purchaseRequisitionSource";
import POApprovalDialog from "./POApprovalDialog";
import "./POApprovalPolicyEditorModal.css";

function policyErrorMessage(error, isSpanish, fallback) {
  const data = error.response?.data;
  if (data?.code === "APPROVER_COVERAGE_REQUIRED") {
    const reference = data.sender_rule_id ? ` (#${data.sender_rule_id})` : "";
    return isSpanish
      ? `La regla de envío${reference} quedaría sin un aprobador que cubra todos sus productos e importes. Crea otra asignación de aprobación que la cubra antes de modificar o eliminar esta.`
      : `The sending rule${reference} would no longer have an approver covering all its products and amounts. Create another covering approver assignment before changing or deleting this one.`;
  }
  return isSpanish ? fallback : data?.error || fallback;
}

const EMPTY_SENDER_FORM = {
  action: "requires_approval",
  scope_level: "all",
  scope_ids: [],
  amount_min: "",
  amount_max: "",
  is_active: true,
};

const EMPTY_APPROVER_FORM = {
  requester_user_id: "",
  requester_source: "",
  scope_level: "all",
  scope_ids: [],
  amount_min: "",
  amount_max: "",
  level_number: 1,
  approval_mode: "any_one",
  fallback_only: false,
  is_active: true,
};

function ruleScopeLevel(rule) {
  if (rule.category_id) return "category";
  if (rule.subfamily_id) return "subfamily";
  if (rule.family_id) return "family";
  return "all";
}

function ruleScopeIds(rule) {
  if (!rule) return [];
  if (rule.category_id) return [String(rule.category_id)];
  if (rule.subfamily_id) return [String(rule.subfamily_id)];
  if (rule.family_id) return [String(rule.family_id)];
  return [];
}

function normalizeScopeIds(...values) {
  return Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) => String(value))
    )
  );
}

export default function POApprovalPolicyEditorModal({
  isOpen,
  onClose,
  onRefresh,
  businessUnitId: businessUnitIdProp,
  initialUserId = null,
  initialMode = "sender",
  users = [],
  families = [],
  subfamilies = [],
  categories = [],
  senderRules = [],
  approverRules = [],
}) {
  const { languageId } = usePmsTheme();
  const user = readStoredUser();
  const [initialBusinessUnitId] = useState(() => user?.active_business_unit_id);
  const businessUnitId = businessUnitIdProp ?? initialBusinessUnitId;
  const canView = hasPurchasingSettingsPermission(user, "orders", "view");
  const canUpdate = Boolean(businessUnitId) && canView && hasPurchasingSettingsPermission(user, "orders", "update");
  const isSpanish = languageId === "es";
  const t = (es, en) => isSpanish ? es : en;
  const ACTION_LABELS = {
    direct_send: t("Enviar directamente", "Send directly"),
    create_without_sending: t("Crear sin enviar", "Create without sending"),
    requires_approval: t("Requiere aprobación", "Requires approval"),
  };
  const APPROVAL_MODE_LABELS = {
    any_one: t("Un aprobador de este nivel", "Any one at this level"),
    all: t("Todos los aprobadores de este nivel", "All approvers at this level"),
  };
  const LEVEL_OPTIONS = [
    { value: 1, label: t("Primero", "First") },
    { value: 2, label: t("Segundo", "Second") },
    { value: 3, label: t("Tercero", "Third") },
    { value: 99, label: t("Último", "Last") },
  ];
  const userLabel = (user, fallback = t("Usuario sin nombre", "Unnamed user")) => user?.name || user?.email || fallback;
  const amountText = (row) => {
    const hasValue = (value) => value !== "" && value != null;
    const format = (value) => Number(value).toLocaleString(isSpanish ? "es-ES" : "en-GB", { maximumFractionDigits: 2 });
    const min = row.amount_min;
    const max = row.amount_max;
    if (hasValue(min) && hasValue(max)) return `${format(min)} – ${format(max)}`;
    if (hasValue(min)) return `${t("Desde", "From")} ${format(min)}`;
    if (hasValue(max)) return `${t("Hasta", "Up to")} ${format(max)}`;
    return t("Cualquier importe", "Any amount");
  };
  const [selectedUserId, setSelectedUserId] = useState("");
  const [activeMode, setActiveMode] = useState("sender");
  const [senderForm, setSenderForm] = useState(EMPTY_SENDER_FORM);
  const [approverForm, setApproverForm] = useState(EMPTY_APPROVER_FORM);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideAction, setOverrideAction] = useState("direct_send");
  const [editingSenderRuleId, setEditingSenderRuleId] = useState(null);
  const [editingApproverRuleId, setEditingApproverRuleId] = useState(null);
  const [scopePicker, setScopePicker] = useState({ open: false, target: null });
  const [scopeSearch, setScopeSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [approverRequesterPreset, setApproverRequesterPreset] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [userPicker, setUserPicker] = useState(null);
  const [userSearch, setUserSearch] = useState("");

  const usersById = useMemo(() => new Map(users.map((user) => [Number(user.id), user])), [users]);
  const subfamiliesById = useMemo(
    () => new Map(subfamilies.map((subfamily) => [Number(subfamily.id), subfamily])),
    [subfamilies]
  );
  const familiesById = useMemo(() => new Map(families.map((family) => [Number(family.id), family])), [families]);
  const categoriesById = useMemo(
    () => new Map(categories.map((category) => [Number(category.id), category])),
    [categories]
  );
  const coverageLookups = useMemo(
    () => ({ usersById, familiesById, subfamiliesById, categoriesById }),
    [usersById, categoriesById, familiesById, subfamiliesById]
  );

  const categoriesWithPath = useMemo(
    () =>
      categories.map((category) => {
        const subfamily = subfamiliesById.get(Number(category.subfamily_id));
        const family = familiesById.get(Number(subfamily?.family_id));
        return {
          ...category,
          path: [family?.name, subfamily?.name, category.name].filter(Boolean).join(" / "),
        };
      }),
    [categories, familiesById, subfamiliesById]
  );

  const selectedUser = usersById.get(Number(selectedUserId));
  const selectedSenderRules = useMemo(
    () =>
      senderRules.filter(
        (rule) => Number(rule.user_id) === Number(selectedUserId) && (rule.rule_type || "sender_rule") === "sender_rule"
      ),
    [selectedUserId, senderRules]
  );
  const selectedOverrideRule = useMemo(
    () =>
      senderRules.find(
        (rule) => Number(rule.user_id) === Number(selectedUserId) && rule.rule_type === "user_override"
      ),
    [selectedUserId, senderRules]
  );
  const selectedApproverRules = useMemo(
    () => approverRules.filter((rule) => Number(rule.approver_user_id) === Number(selectedUserId)),
    [approverRules, selectedUserId]
  );

  const resetRuleEditors = () => {
    setEditingSenderRuleId(null);
    setEditingApproverRuleId(null);
    setSenderForm(EMPTY_SENDER_FORM);
    setApproverForm(EMPTY_APPROVER_FORM);
    setScopePicker({ open: false, target: null });
    setScopeSearch("");
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedUserId(initialUserId ? String(initialUserId) : "");
    setActiveMode(initialMode === "approver" ? "approver" : "sender");
    setOverrideOpen(initialMode === "override");
    setOverrideEnabled(false);
    setOverrideAction("direct_send");
    setError("");
    setHelpOpen(false);
    setDeleteTarget(null);
    setUserPicker(null);
    setUserSearch("");
    setApproverRequesterPreset("");
    resetRuleEditors();
  }, [initialMode, initialUserId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    resetRuleEditors();
    setOverrideEnabled(Boolean(selectedOverrideRule));
    setOverrideAction(selectedOverrideRule?.action || "direct_send");
  }, [isOpen, selectedOverrideRule, selectedUserId]);

  useEffect(() => {
    if (!isOpen || activeMode !== "approver" || !selectedUserId || !approverRequesterPreset) {
      return;
    }
    setApproverForm((current) => ({
      ...current,
      requester_user_id: approverRequesterPreset,
      requester_source: "",
    }));
    setApproverRequesterPreset("");
  }, [activeMode, approverRequesterPreset, isOpen, selectedUserId]);

  const scopeOptions = (scopeLevel) => {
    if (scopeLevel === "family") return families;
    if (scopeLevel === "subfamily") return subfamilies;
    if (scopeLevel === "category") return categoriesWithPath;
    return [];
  };

  const scopeLabel = (scopeLevel) => {
    if (scopeLevel === "family") return t("Familia", "Family");
    if (scopeLevel === "subfamily") return t("Subfamilia", "Subfamily");
    if (scopeLevel === "category") return t("Categoría", "Category");
    return t("Productos", "Products");
  };

  const getScopeOptionLabel = (scopeLevel, option) => {
    if (!option) return "-";
    if (scopeLevel === "category") return option.path || option.name || `${t("Categoría", "Category")} #${option.id}`;
    return option.name || `${scopeLabel(scopeLevel)} #${option.id}`;
  };

  const getScopeIds = (row) => normalizeScopeIds(row?.scope_ids, row?.scope_id, ruleScopeIds(row));

  const getScopeText = (row) => {
    const level = row?.scope_level || ruleScopeLevel(row);
    if (level === "all") return t("Todos los productos", "All products");

    const optionsById = new Map(scopeOptions(level).map((option) => [String(option.id), option]));
    const labels = getScopeIds(row)
      .map((id) => getScopeOptionLabel(level, optionsById.get(String(id))))
      .filter((label) => label && label !== "-");

    if (!labels.length && row?.category) labels.push(row.category.name);
    if (!labels.length && row?.subfamily) labels.push(row.subfamily.name);
    if (!labels.length && row?.family) labels.push(row.family.name);
    return labels.length ? `${scopeLabel(level)}: ${labels.join(", ")}` : `${scopeLabel(level)}: —`;
  };

  const getScopeSelectionSummary = (form) => {
    if (form.scope_level === "all") return t("Todos los productos", "All products");
    const ids = getScopeIds(form);
    if (!ids.length) return `${t("Seleccionar", "Select")} ${scopeLabel(form.scope_level).toLowerCase()}…`;
    const optionsById = new Map(scopeOptions(form.scope_level).map((option) => [String(option.id), option]));
    const labels = ids.map((id) => getScopeOptionLabel(form.scope_level, optionsById.get(String(id))));
    return labels.length <= 2 ? labels.join(", ") : `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  };

  const buildScopePayload = (form) => {
    const payload = { family_id: null, subfamily_id: null, category_id: null };
    if (form.scope_level === "all") return payload;
    const scopeId = getScopeIds(form)[0] || "";
    if (form.scope_level === "family") payload.family_id = scopeId;
    if (form.scope_level === "subfamily") payload.subfamily_id = scopeId;
    if (form.scope_level === "category") payload.category_id = scopeId;
    return payload;
  };

  const scopeIdsToSave = (form) => (form.scope_level === "all" ? [null] : getScopeIds(form));
  const formForScopeId = (form, scopeId) => ({ ...form, scope_ids: scopeId ? [String(scopeId)] : [] });
  const hasScopeSelection = (form) => form.scope_level === "all" || getScopeIds(form).length > 0;

  const senderCoverageRequired =
    senderForm.action === "requires_approval" && senderForm.is_active;
  const senderCoverageChecks = useMemo(() => {
    if (!senderCoverageRequired || !selectedUserId) return [];

    const scopeIds = senderForm.scope_level === "all"
      ? [null]
      : normalizeScopeIds(senderForm.scope_ids);

    return scopeIds.map((scopeId) => {
      const scopedForm = {
        ...senderForm,
        scope_ids: scopeId ? [String(scopeId)] : [],
      };
      const scopePayload = { family_id: null, subfamily_id: null, category_id: null };
      if (scopedForm.scope_level === "family") scopePayload.family_id = scopeId;
      if (scopedForm.scope_level === "subfamily") scopePayload.subfamily_id = scopeId;
      if (scopedForm.scope_level === "category") scopePayload.category_id = scopeId;

      const senderRule = {
        ...scopePayload,
        user_id: selectedUserId,
        action: senderForm.action,
        is_active: senderForm.is_active,
        amount_min: senderForm.amount_min,
        amount_max: senderForm.amount_max,
      };

      return getCoveringApproverRules(senderRule, approverRules, coverageLookups);
    });
  }, [approverRules, coverageLookups, selectedUserId, senderCoverageRequired, senderForm]);
  const senderHasCompleteCoverage =
    !senderCoverageRequired ||
    (senderCoverageChecks.length > 0 && senderCoverageChecks.every((rules) => rules.length > 0));
  const coveringApproverNames = useMemo(
    () =>
      Array.from(
        new Set(
          senderCoverageChecks
            .flat()
            .map((rule) =>
              `${rule.approver?.name || rule.approver?.email || `${isSpanish ? "Usuario" : "User"} #${rule.approver_user_id}`}${
                rule.fallback_only ? (isSpanish ? " (suplente)" : " (fallback)") : ""
              }`
            )
        )
      ),
    [isSpanish, senderCoverageChecks]
  );

  const openScopePicker = (target) => {
    setScopeSearch("");
    setScopePicker({ open: true, target });
  };

  const setScopeIdsForTarget = (target, updater) => {
    const setForm = target === "approver" ? setApproverForm : setSenderForm;
    setForm((current) => {
      const next = typeof updater === "function" ? updater(getScopeIds(current)) : updater;
      return { ...current, scope_ids: normalizeScopeIds(next) };
    });
  };

  const beginApproverSetup = () => {
    setApproverRequesterPreset(String(selectedUserId));
    setSelectedUserId("");
    setActiveMode("approver");
    setError("");
  };

  const saveUserOverride = async () => {
    if (!canUpdate || !selectedUserId || saving) return;
    setSaving(true);
    setError("");
    try {
      if (!overrideEnabled) {
        if (selectedOverrideRule) {
          await axios.delete(`/api/purchasing/po-approval-rules/rules/${selectedOverrideRule.id}?business_unit_id=${businessUnitId}`);
        }
      } else {
        const form = { action: overrideAction, scope_level: "all", scope_ids: [], amount_min: "", amount_max: "" };
        const payload = {
          user_id: selectedUserId,
          rule_type: "user_override",
          name: `${userLabel(selectedUser)} - ${t("Excepción del usuario", "User override")}`.slice(0, 160),
          action: overrideAction,
          priority: 100,
          amount_min: "",
          amount_max: "",
          is_active: true,
          ...buildScopePayload(form),
          steps: [],
        };
        if (selectedOverrideRule) {
          await axios.put(`/api/purchasing/po-approval-rules/rules/${selectedOverrideRule.id}?business_unit_id=${businessUnitId}`, payload);
        } else {
          await axios.post(`/api/purchasing/po-approval-rules/rules?business_unit_id=${businessUnitId}`, payload);
        }
      }
      await onRefresh();
    } catch (saveError) {
      setError(policyErrorMessage(saveError, isSpanish, t("No se ha podido guardar la excepción del usuario.", "Could not save the user override.")));
    } finally {
      setSaving(false);
    }
  };

  const validAmountRange = (form) => {
    const min = form.amount_min;
    const max = form.amount_max;
    const isSet = (value) => value !== "" && value != null;
    return (!isSet(min) || (Number.isFinite(Number(min)) && Number(min) >= 0)) &&
      (!isSet(max) || (Number.isFinite(Number(max)) && Number(max) >= 0)) &&
      (!isSet(min) || !isSet(max) || Number(min) <= Number(max));
  };

  const saveSenderRule = async () => {
    if (!canUpdate || !selectedUserId || !hasScopeSelection(senderForm) || saving || !validAmountRange(senderForm)) return;
    if (!senderHasCompleteCoverage) {
      setError(
        t("Primero crea una asignación de aprobador activa que cubra al solicitante, los productos y todo el intervalo de importes.", "Create an active approver assignment that covers this requester, product scope and full amount range first.")
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const scopeIds = scopeIdsToSave(senderForm);
      await Promise.all(
        scopeIds.map((scopeId, index) => {
          const scopedForm = formForScopeId(senderForm, scopeId);
          const payload = {
            user_id: selectedUserId,
            rule_type: "sender_rule",
            name: `${userLabel(selectedUser)} - ${ACTION_LABELS[senderForm.action]} - ${getScopeText(scopedForm)} - ${amountText(scopedForm)}`.slice(0, 160),
            action: senderForm.action,
            priority: 100,
            amount_min: senderForm.amount_min,
            amount_max: senderForm.amount_max,
            is_active: senderForm.is_active,
            ...buildScopePayload(scopedForm),
            steps: [],
          };
          if (editingSenderRuleId && index === 0) {
            return axios.put(`/api/purchasing/po-approval-rules/rules/${editingSenderRuleId}?business_unit_id=${businessUnitId}`, payload);
          }
          return axios.post(`/api/purchasing/po-approval-rules/rules?business_unit_id=${businessUnitId}`, payload);
        })
      );
      await onRefresh();
      setEditingSenderRuleId(null);
      setSenderForm(EMPTY_SENDER_FORM);
    } catch (saveError) {
      setError(policyErrorMessage(saveError, isSpanish, t("No se ha podido guardar la regla de envío.", "Could not save the sender rule.")));
    } finally {
      setSaving(false);
    }
  };

  const saveApproverRule = async () => {
    if (!canUpdate || !selectedUserId || !hasScopeSelection(approverForm) || saving || !validAmountRange(approverForm)) return;
    setSaving(true);
    setError("");
    try {
      const scopeIds = scopeIdsToSave(approverForm);
      await Promise.all(
        scopeIds.map((scopeId, index) => {
          const scopedForm = formForScopeId(approverForm, scopeId);
          const payload = {
            approver_user_id: selectedUserId,
            requester_user_id: approverForm.requester_source ? null : approverForm.requester_user_id || null,
            requester_source: approverForm.requester_source || null,
            level_number: approverForm.level_number,
            approval_mode: approverForm.approval_mode,
            amount_min: approverForm.amount_min,
            amount_max: approverForm.amount_max,
            fallback_only: approverForm.fallback_only,
            is_active: approverForm.is_active,
            ...buildScopePayload(scopedForm),
          };
          if (editingApproverRuleId && index === 0) {
            return axios.put(`/api/purchasing/po-approval-rules/approver-rules/${editingApproverRuleId}?business_unit_id=${businessUnitId}`, payload);
          }
          return axios.post(`/api/purchasing/po-approval-rules/approver-rules?business_unit_id=${businessUnitId}`, payload);
        })
      );
      await onRefresh();
      setEditingApproverRuleId(null);
      setApproverForm(EMPTY_APPROVER_FORM);
    } catch (saveError) {
      setError(policyErrorMessage(saveError, isSpanish, t("No se ha podido guardar la asignación de aprobador.", "Could not save the approver assignment.")));
    } finally {
      setSaving(false);
    }
  };

  const deleteSenderRule = async (rule) => {
    if (!canUpdate || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.delete(`/api/purchasing/po-approval-rules/rules/${rule.id}?business_unit_id=${businessUnitId}`);
      await onRefresh();
      setDeleteTarget(null);
      if (Number(editingSenderRuleId) === Number(rule.id)) {
        setEditingSenderRuleId(null);
        setSenderForm(EMPTY_SENDER_FORM);
      }
    } catch (deleteError) {
      setError(policyErrorMessage(deleteError, isSpanish, t("No se ha podido eliminar la regla de envío.", "Could not delete the sender rule.")));
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const deleteApproverRule = async (rule) => {
    if (!canUpdate || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.delete(`/api/purchasing/po-approval-rules/approver-rules/${rule.id}?business_unit_id=${businessUnitId}`);
      await onRefresh();
      setDeleteTarget(null);
      if (Number(editingApproverRuleId) === Number(rule.id)) {
        setEditingApproverRuleId(null);
        setApproverForm(EMPTY_APPROVER_FORM);
      }
    } catch (deleteError) {
      setError(policyErrorMessage(deleteError, isSpanish, t("No se ha podido eliminar la asignación de aprobador.", "Could not delete the approver assignment.")));
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const renderScopeFields = (form, setForm, target) => (
    <div className="poep-fieldGrid">
      <label className="poep-field">
        <span>{t("Ámbito de productos", "Product scope")}</span>
        <select value={form.scope_level} onChange={(event) => setForm((current) => ({ ...current, scope_level: event.target.value, scope_ids: [] }))}>
          <option value="all">{t("Todos los productos", "All products")}</option>
          <option value="family">{t("Familia", "Family")}</option>
          <option value="subfamily">{t("Subfamilia", "Subfamily")}</option>
          <option value="category">{t("Categoría", "Category")}</option>
        </select>
      </label>
      {form.scope_level !== "all" ? (
        <div className="poep-field">
          <span id={`poep-${target}-selection`}>{t("Selección de productos", "Product selection")}</span>
          <button type="button" className="poep-scopeButton" aria-labelledby={`poep-${target}-selection`} onClick={() => openScopePicker(target)}>
            <span>{getScopeSelectionSummary(form)}</span><strong>{getScopeIds(form).length}</strong>
          </button>
        </div>
      ) : <p className="poep-fieldHint">{t("Incluye todas las familias, subfamilias y categorías.", "Includes every family, subfamily and category.")}</p>}
    </div>
  );

  const renderAmountFields = (form, setForm) => (
    <>
      <div className="poep-fieldGrid">
        <label className="poep-field"><span>{t("Importe mínimo", "Minimum amount")}</span><input type="number" min="0" step="0.01" placeholder={t("Sin mínimo", "No minimum")} value={form.amount_min} onChange={(event) => setForm((current) => ({ ...current, amount_min: event.target.value }))} /></label>
        <label className="poep-field"><span>{t("Importe máximo", "Maximum amount")}</span><input type="number" min="0" step="0.01" placeholder={t("Sin máximo", "No maximum")} value={form.amount_max} onChange={(event) => setForm((current) => ({ ...current, amount_max: event.target.value }))} /></label>
      </div>
      {!validAmountRange(form) ? <p className="poep-validation" role="alert">{t("Los importes deben ser positivos o cero, y el máximo no puede ser inferior al mínimo.", "Amounts must be zero or positive, and the maximum cannot be lower than the minimum.")}</p> : <p className="poep-hint">{t("En blanco: sin límite de importe.", "Leave blank for no amount limit.")}</p>}
    </>
  );

  const editSender = (rule) => {
    setEditingSenderRuleId(rule.id);
    setSenderForm({ action: rule.action || "requires_approval", scope_level: ruleScopeLevel(rule), scope_ids: ruleScopeIds(rule), amount_min: rule.amount_min ?? "", amount_max: rule.amount_max ?? "", is_active: rule.is_active !== false });
    setError("");
  };
  const editApprover = (rule) => {
    setEditingApproverRuleId(rule.id);
    setApproverForm({ requester_user_id: rule.requester_user_id ? String(rule.requester_user_id) : "", requester_source: rule.requester_source || "", scope_level: ruleScopeLevel(rule), scope_ids: ruleScopeIds(rule), amount_min: rule.amount_min ?? "", amount_max: rule.amount_max ?? "", level_number: Number(rule.level_number || 1), approval_mode: rule.approval_mode || "any_one", fallback_only: Boolean(rule.fallback_only), is_active: rule.is_active !== false });
    setError("");
  };
  const ruleActions = (rule, target) => (
    <div className="poep-ruleActions">
      <button type="button" className="poep-iconButton" onClick={() => target === "sender" ? editSender(rule) : editApprover(rule)} aria-label={target === "sender" ? t("Editar regla de envío", "Edit sender rule") : t("Editar asignación de aprobador", "Edit approver assignment")} title={t("Editar", "Edit")}><FiEdit3 aria-hidden="true" /></button>
      <button type="button" className="poep-iconButton poep-iconButton--danger" onClick={() => setDeleteTarget({ target, rule })} aria-label={target === "sender" ? t("Eliminar regla de envío", "Delete sender rule") : t("Eliminar asignación de aprobador", "Delete approver assignment")} title={t("Eliminar", "Delete")}><FiTrash2 aria-hidden="true" /></button>
    </div>
  );
  const activeLabel = (rule) => (
    <span className={`poep-ruleState ${rule.is_active === false ? "poep-ruleState--inactive" : ""}`}>{rule.is_active === false ? t("Inactiva", "Inactive") : t("Activa", "Active")}</span>
  );

  const openUserPicker = (target) => {
    if (saving) return;
    setUserSearch("");
    setUserPicker(target);
  };
  const chooseUser = (id) => {
    if (saving) return;
    if (userPicker === "requester") setApproverForm((current) => ({ ...current, requester_user_id: id, requester_source: "" }));
    else { setSelectedUserId(id); setError(""); }
    setUserPicker(null);
  };
  const normalizeSearch = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const requester = usersById.get(Number(approverForm.requester_user_id));
  const sourceLabel = parStockCoverageLabel(isSpanish ? "es" : "en");
  const requesterLabel = (rule, user) => rule.requester_source === "par_stock" ? sourceLabel
    : rule.requester_user_id ? userLabel(user || rule.requester || usersById.get(Number(rule.requester_user_id)), `${t("Usuario", "User")} #${rule.requester_user_id}`)
      : t("Todos los solicitantes", "All requesters");

  if (!isOpen || !canView) return null;
  if (!canUpdate) return <POApprovalDialog isOpen onClose={onClose} title={languageId === "ca" ? "Política de comandes" : t("Política de pedidos", "Purchase order policy")}
    footer={<button type="button" className="poep-button" onClick={onClose}>{languageId === "ca" ? "Tancar" : t("Cerrar", "Close")}</button>}>
    <p role="status">{languageId === "ca" ? "Només lectura. Necessites permís per modificar les polítiques d'aprovació i enviament de comandes." : t("Solo lectura. Necesitas permiso para modificar las políticas de aprobación y envío de pedidos.", "Read-only. You need permission to modify purchase order approval and sending policies.")}</p>
  </POApprovalDialog>;
  const activeScopeForm = scopePicker.target === "approver" ? approverForm : senderForm;
  const activeScopeIds = getScopeIds(activeScopeForm);
  const filteredScopeOptions = scopeOptions(activeScopeForm.scope_level).filter((option) => {
    const query = scopeSearch.trim().toLowerCase();
    return !query || getScopeOptionLabel(activeScopeForm.scope_level, option).toLowerCase().includes(query);
  });
  const closePicker = () => setScopePicker({ open: false, target: null });
  const selectedPickerUserId = userPicker === "requester" ? approverForm.requester_source ? `source:${approverForm.requester_source}` : approverForm.requester_user_id : selectedUserId;
  const filteredUsers = users.filter((user) => normalizeSearch(`${user.name || ""} ${user.email || ""}`).includes(normalizeSearch(userSearch)));

  return (
    <>
      <POApprovalDialog
        isOpen={isOpen}
        onClose={onClose}
        busy={saving}
        width="wide"
        className="poep-editor"
        title={selectedUser ? `${t("Política de pedidos", "Purchase order policy")} · ${userLabel(selectedUser)}` : t("Nueva política de pedidos", "New purchase order policy")}
        subtitle={t("Configura qué puede enviar cada persona y qué pedidos puede aprobar.", "Define what each person can send and which orders they can approve.")}
        footer={<><span className="poep-saveHint" role="status">{saving ? t("Guardando cambios…", "Saving changes…") : t("Cada regla se guarda por separado. Cerrar no deshace los cambios guardados.", "Each rule is saved separately. Closing does not undo saved changes.")}</span><button type="button" className="poep-button" disabled={saving} onClick={onClose}>{t("Cerrar", "Close")}</button></>}
      >
        <fieldset className="poep-controls" disabled={saving}>
          <div className="poep-toolbar">
            <div className="poep-field poep-userField">
              <span id="poep-policy-user-label">{activeMode === "sender" ? t("Solicitante / remitente", "Requester / sender") : t("Aprobador", "Approver")}</span>
              <button type="button" className="poep-userButton" aria-labelledby="poep-policy-user-label" aria-haspopup="dialog" value={selectedUserId} onClick={() => openUserPicker("user")}>
                <span>{selectedUser ? userLabel(selectedUser) : activeMode === "sender" ? t("Selecciona al solicitante…", "Select requester…") : t("Selecciona al aprobador…", "Select approver…")}</span><FiChevronDown aria-hidden="true" />
              </button>
            </div>
            <button type="button" className="poep-button poep-helpButton" onClick={() => setHelpOpen(true)}><FiHelpCircle aria-hidden="true" />{t("Cómo funciona", "How it works")}</button>
          </div>
          <div className="poep-tabs" role="tablist" aria-label={t("Tipo de política", "Policy type")}>
            {["sender", "approver"].map((mode) => (
              <button key={mode} id={`poep-tab-${mode}`} type="button" role="tab" aria-selected={activeMode === mode} aria-controls="poep-policy-panel" className={`poep-tab ${activeMode === mode ? "poep-tab--active" : ""}`} onClick={() => { setActiveMode(mode); setError(""); }}>
                {mode === "sender" ? t("Permisos de envío", "Sending permissions") : t("Asignaciones de aprobación", "Approver assignments")}
              </button>
            ))}
          </div>
          {error ? <div className="poep-error" role="alert">{error}</div> : null}
          <div id="poep-policy-panel" role="tabpanel" aria-labelledby={`poep-tab-${activeMode}`}>
            {!selectedUserId ? <div className="poep-empty poep-empty--large">{activeMode === "sender" ? t("Selecciona a la persona que creará o enviará los pedidos.", "Select the person who will create or send purchase orders.") : t("Selecciona a la persona que decidirá sobre los pedidos de otros usuarios.", "Select the person who will decide on other users’ purchase orders.")}</div> : activeMode === "sender" ? (
              <>
                <div className="poep-editGrid">
                  <section className="poep-section">
                    <div className="poep-sectionHead"><h3>{editingSenderRuleId ? t("Editar regla de envío", "Edit sender rule") : t("Nueva regla de envío", "New sender rule")}</h3></div>
                    <fieldset className="poep-actionSelector">
                      <legend>{t("Acción permitida", "Allowed action")}</legend>
                      <div className="poep-actionSegments">
                        {Object.entries(ACTION_LABELS).map(([value, label]) => (
                          <label className="poep-actionOption" key={value}>
                            <input
                              type="radio"
                              name="poep-sender-action"
                              value={value}
                              checked={senderForm.action === value}
                              onChange={(event) => setSenderForm((current) => ({ ...current, action: event.target.value }))}
                            />
                            <span className="poep-actionLabel"><FiCheck aria-hidden="true" /><span>{label}</span></span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {renderScopeFields(senderForm, setSenderForm, "sender")}
                    {renderAmountFields(senderForm, setSenderForm)}
                    <label className="poep-check"><input type="checkbox" checked={senderForm.is_active} onChange={(event) => setSenderForm((current) => ({ ...current, is_active: event.target.checked }))} />{t("Regla activa", "Active rule")}</label>
                    {senderCoverageRequired ? (
                      <div className={`poep-coverage ${senderHasCompleteCoverage ? "poep-coverage--ok" : "poep-coverage--missing"}`} role="status">
                        <strong>{senderHasCompleteCoverage ? t("Aprobadores disponibles", "Approver coverage confirmed") : t("Falta cobertura de aprobación", "Missing approver coverage")}</strong>
                        <p>{senderHasCompleteCoverage ? `${t("Cubierto por", "Covered by")} ${coveringApproverNames.join(", ")}.` : t("Cada ámbito y todo el intervalo de importes deben tener un aprobador antes de activar esta regla.", "Every scope and the full amount range need an approver before this rule can be activated.")}</p>
                        {!senderHasCompleteCoverage ? <button type="button" className="poep-button" onClick={beginApproverSetup}>{t("Configurar un aprobador", "Set up an approver")}</button> : null}
                      </div>
                    ) : null}
                    <div className="poep-formActions">
                      <button type="button" className="poep-button poep-button--primary" disabled={saving || !hasScopeSelection(senderForm) || !senderHasCompleteCoverage || !validAmountRange(senderForm)} onClick={saveSenderRule}>{editingSenderRuleId ? t("Guardar regla", "Save rule") : t("Crear regla", "Create rule")}</button>
                      {editingSenderRuleId ? <button type="button" className="poep-button" onClick={resetRuleEditors}>{t("Cancelar edición", "Cancel edit")}</button> : null}
                    </div>
                    {getScopeIds(senderForm).length > 1 ? <p className="poep-hint">{t("Se guardará una regla por cada selección de productos.", "One rule will be saved for each product selection.")}</p> : null}
                  </section>
                  <section className="poep-section poep-savedSection">
                    <div className="poep-sectionHead"><h3>{t("Reglas de envío guardadas", "Saved sending rules")}</h3><span className="poep-count">{selectedSenderRules.length}</span></div>
                    <div className="poep-ruleList">
                      {selectedSenderRules.length ? selectedSenderRules.map((rule) => (
                        <article className={`poep-rule ${Number(editingSenderRuleId) === Number(rule.id) ? "poep-rule--editing" : ""}`} key={rule.id}>
                          <div className="poep-ruleContent"><div className="poep-ruleTitle"><strong>{ACTION_LABELS[rule.action] || t("Regla de envío", "Sending rule")}</strong>{activeLabel(rule)}</div><p>{getScopeText(rule)}</p><p>{amountText(rule)}</p></div>
                          {ruleActions(rule, "sender")}
                        </article>
                      )) : <div className="poep-empty">{t("No hay reglas de envío guardadas para esta persona.", "No sending rules saved for this person.")}</div>}
                    </div>
                  </section>
                </div>
                <details className="poep-override" open={overrideOpen} onToggle={(event) => setOverrideOpen(event.currentTarget.open)}>
                  <summary onClick={(event) => { if (saving) event.preventDefault(); }}>{t("Avanzado · Excepción de envío del usuario", "Advanced · User sending override")}<span>{selectedOverrideRule ? t("Configurada", "Configured") : t("Sin excepción", "No override")}</span></summary>
                  <p className="poep-hint">{t("Añade una regla general para esta persona, sin límites de producto o importe. Evita reglas generales contradictorias; las reglas con un ámbito más específico tienen prioridad.", "Adds a general rule for this person, with no product or amount limits. Avoid conflicting general rules; rules with a more specific product scope take priority.")}</p>
                  <div className="poep-overrideFields">
                    <label className="poep-check"><input type="checkbox" checked={overrideEnabled} onChange={(event) => setOverrideEnabled(event.target.checked)} />{t("Usar una excepción para esta persona", "Use an override for this person")}</label>
                    <label className="poep-field"><span>{t("Acción de la excepción", "Override action")}</span><select value={overrideAction} disabled={!overrideEnabled} onChange={(event) => setOverrideAction(event.target.value)}><option value="direct_send">{ACTION_LABELS.direct_send}</option><option value="create_without_sending">{ACTION_LABELS.create_without_sending}</option></select></label>
                    <button type="button" className="poep-button" onClick={saveUserOverride}>{t("Guardar excepción", "Save override")}</button>
                  </div>
                </details>
              </>
            ) : (
              <div className="poep-editGrid">
                <section className="poep-section">
                  <div className="poep-sectionHead"><h3>{editingApproverRuleId ? t("Editar asignación", "Edit assignment") : t("Nueva asignación", "New assignment")}</h3></div>
                  <div className="poep-fieldGrid">
                    <label className="poep-field"><span>{t("Tipo de aprobador", "Approver type")}</span><select value={approverForm.fallback_only ? "fallback" : "standard"} onChange={(event) => setApproverForm((current) => ({ ...current, fallback_only: event.target.value === "fallback" }))}><option value="standard">{t("Habitual", "Standard")}</option><option value="fallback">{t("Suplente", "Fallback")}</option></select></label>
                    <div className="poep-field"><span id="poep-requester-label">{t("Pedidos del solicitante", "Orders from requester")}</span><button type="button" className="poep-userButton" aria-labelledby="poep-requester-label" aria-haspopup="dialog" value={approverForm.requester_user_id} onClick={() => openUserPicker("requester")}><span>{requesterLabel(approverForm, requester)}</span><FiChevronDown aria-hidden="true" /></button></div>
                  </div>
                  <p className="poep-typeNotice">{approverForm.fallback_only ? t("El suplente interviene solo cuando no coincide ninguna asignación habitual.", "Fallback is used only when no standard assignment matches.") : t("Se evalúa primero si coinciden solicitante, productos e importe.", "Evaluated first when requester, products and amount match.")}</p>
                  <div className="poep-fieldGrid">
                    <label className="poep-field"><span>{t("Nivel de aprobación", "Approval level")}</span><select value={approverForm.level_number} onChange={(event) => setApproverForm((current) => ({ ...current, level_number: Number(event.target.value) }))}>{LEVEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                    <label className="poep-field"><span>{t("Decisión necesaria", "Decision mode")}</span><select value={approverForm.approval_mode} onChange={(event) => setApproverForm((current) => ({ ...current, approval_mode: event.target.value }))}>{Object.entries(APPROVAL_MODE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  </div>
                  {renderScopeFields(approverForm, setApproverForm, "approver")}
                  {renderAmountFields(approverForm, setApproverForm)}
                  <label className="poep-check"><input type="checkbox" checked={approverForm.is_active} onChange={(event) => setApproverForm((current) => ({ ...current, is_active: event.target.checked }))} />{t("Asignación activa", "Active assignment")}</label>
                  <div className="poep-formActions">
                    <button type="button" className="poep-button poep-button--primary" disabled={saving || !hasScopeSelection(approverForm) || !validAmountRange(approverForm)} onClick={saveApproverRule}>{editingApproverRuleId ? t("Guardar asignación", "Save assignment") : t("Crear asignación", "Create assignment")}</button>
                    {editingApproverRuleId ? <button type="button" className="poep-button" onClick={resetRuleEditors}>{t("Cancelar edición", "Cancel edit")}</button> : null}
                  </div>
                  {getScopeIds(approverForm).length > 1 ? <p className="poep-hint">{t("Se guardará una asignación por cada selección de productos.", "One assignment will be saved for each product selection.")}</p> : null}
                </section>
                <section className="poep-section poep-savedSection">
                  <div className="poep-sectionHead"><h3>{t("Asignaciones guardadas", "Saved assignments")}</h3><span className="poep-count">{selectedApproverRules.length}</span></div>
                  <div className="poep-ruleList">
                    {selectedApproverRules.length ? selectedApproverRules.map((rule) => (
                      <article className={`poep-rule ${Number(editingApproverRuleId) === Number(rule.id) ? "poep-rule--editing" : ""}`} key={rule.id}>
                        <div className="poep-ruleContent">
                          <div className="poep-ruleTitle"><strong>{rule.fallback_only ? `${t("Suplente", "Fallback")} · ` : ""}{t("Nivel", "Level")} {LEVEL_OPTIONS.find((option) => Number(option.value) === Number(rule.level_number))?.label || rule.level_number}</strong>{activeLabel(rule)}</div>
                          <p>{t("Solicitante", "Requester")}: {requesterLabel(rule)}</p>
                          <p>{getScopeText(rule)} · {amountText(rule)}</p>
                          <p>{APPROVAL_MODE_LABELS[rule.approval_mode] || APPROVAL_MODE_LABELS.any_one}</p>
                        </div>
                        {ruleActions(rule, "approver")}
                      </article>
                    )) : <div className="poep-empty">{t("Esta persona todavía no tiene asignaciones de aprobación.", "This person has no approver assignments yet.")}</div>}
                  </div>
                </section>
              </div>
            )}
          </div>
        </fieldset>
      </POApprovalDialog>

      <POApprovalDialog isOpen={Boolean(userPicker)} onClose={() => setUserPicker(null)} busy={saving} className="poep-userPicker" title={userPicker === "requester" || activeMode === "sender" ? t("Seleccionar solicitante", "Select requester") : t("Seleccionar aprobador", "Select approver")} subtitle={userPicker === "requester" ? t("Elige un origen automático, todos los solicitantes o una persona.", "Choose an automatic source, all requesters or a person.") : t("Busca por nombre o correo y elige una persona.", "Search by name or email and choose a person.")} footer={<button type="button" className="poep-button" disabled={saving} onClick={() => setUserPicker(null)}>{t("Cancelar", "Cancel")}</button>}>
        <label className="poep-field"><span>{t("Buscar personas", "Search people")}</span><input type="search" value={userSearch} disabled={saving} onChange={(event) => setUserSearch(event.target.value)} placeholder={t("Nombre o correo electrónico…", "Name or email address…")} /></label>
        <div className="poep-userOptions">
          {userPicker === "requester" ? <button type="button" className={`poep-userOption ${!selectedPickerUserId ? "poep-userOption--selected" : ""}`} data-user-id="" aria-pressed={!selectedPickerUserId} disabled={saving} onClick={() => chooseUser("")}><span><strong>{t("Todos los solicitantes", "All requesters")}</strong><small>{t("Incluye personas y solicitudes automáticas.", "Includes people and automatic requisitions.")}</small></span>{!selectedPickerUserId && <FiCheck aria-hidden="true" />}</button> : null}
          {userPicker === "requester" && normalizeSearch(sourceLabel).includes(normalizeSearch(userSearch)) && <button type="button"
            className={`poep-userOption ${approverForm.requester_source === "par_stock" ? "poep-userOption--selected" : ""}`}
            data-requester-source="par_stock" aria-pressed={approverForm.requester_source === "par_stock"} disabled={saving}
            onClick={() => { setApproverForm((current) => ({ ...current, requester_user_id: "", requester_source: "par_stock" })); setUserPicker(null); }}>
            <span><strong>{sourceLabel}</strong><small>{t("Origen automático · solicitudes generadas para cubrir el stock objetivo.", "Automatic source · requisitions generated to cover par stock.")}</small></span>
            {approverForm.requester_source === "par_stock" && <FiCheck aria-hidden="true" />}
          </button>}
          {filteredUsers.map((user) => {
            const selected = String(user.id) === selectedPickerUserId;
            return <button type="button" className={`poep-userOption ${selected ? "poep-userOption--selected" : ""}`} key={user.id} data-user-id={user.id} aria-pressed={selected} disabled={saving} onClick={() => chooseUser(String(user.id))}><span><strong>{userLabel(user)}</strong>{user.email && <small>{user.email}</small>}{user.is_active === false && <small>{t("Usuario inactivo", "Inactive user")}</small>}</span>{selected && <FiCheck aria-hidden="true" />}</button>;
          })}
          {!filteredUsers.length && !(userPicker === "requester" && normalizeSearch(sourceLabel).includes(normalizeSearch(userSearch))) ? <div className="poep-empty">{t("No hay personas que coincidan con la búsqueda.", "No people match this search.")}</div> : null}
        </div>
      </POApprovalDialog>

      <POApprovalDialog isOpen={scopePicker.open && activeScopeForm.scope_level !== "all"} onClose={closePicker} busy={saving} className="poep-picker" title={`${t("Seleccionar", "Select")} · ${scopeLabel(activeScopeForm.scope_level)}`} subtitle={t("Marca uno o varios productos. La selección se aplica al formulario; no se guarda hasta guardar la regla.", "Select one or more products. Selections apply to the form; nothing is saved until you save the rule.")} footer={<><span className="poep-saveHint">{activeScopeIds.length} {t("seleccionados", "selected")}</span><button type="button" className="poep-button poep-button--primary" onClick={closePicker}>{t("Listo", "Done")}</button></>}>
        <label className="poep-field"><span>{t("Buscar productos", "Search products")}</span><input className="poep-search" value={scopeSearch} onChange={(event) => setScopeSearch(event.target.value)} placeholder={t("Nombre de familia, subfamilia o categoría…", "Family, subfamily or category name…")} /></label>
        <div className="poep-pickerToolbar"><span>{filteredScopeOptions.length} {t("resultados", "results")}</span><button type="button" className="poep-button" disabled={!filteredScopeOptions.length} onClick={() => setScopeIdsForTarget(scopePicker.target, (ids) => normalizeScopeIds(ids, filteredScopeOptions.map((option) => option.id)))}>{t("Seleccionar visibles", "Select visible")}</button><button type="button" className="poep-button" disabled={!activeScopeIds.length} onClick={() => setScopeIdsForTarget(scopePicker.target, [])}>{t("Limpiar selección", "Clear selection")}</button></div>
        <div className="poep-pickerList">{filteredScopeOptions.length ? filteredScopeOptions.map((option) => {
          const checked = activeScopeIds.includes(String(option.id));
          return <label className={`poep-pickerItem ${checked ? "poep-pickerItem--selected" : ""}`} key={option.id}><input type="checkbox" checked={checked} onChange={() => setScopeIdsForTarget(scopePicker.target, (ids) => checked ? ids.filter((id) => id !== String(option.id)) : [...ids, String(option.id)])} /><span>{getScopeOptionLabel(activeScopeForm.scope_level, option)}</span></label>;
        }) : <div className="poep-empty">{t("No hay productos que coincidan con la búsqueda.", "No products match this search.")}</div>}</div>
      </POApprovalDialog>

      <POApprovalDialog isOpen={helpOpen} onClose={() => setHelpOpen(false)} className="poep-help" title={t("Cómo se aprueban y envían los pedidos", "How purchase orders are approved and sent")} footer={<button type="button" className="poep-button poep-button--primary" onClick={() => setHelpOpen(false)}>{t("Entendido", "Got it")}</button>}>
        <section><h3>{t("Orden de evaluación", "Evaluation order")}</h3><ol>
          <li>{t("Se buscan reglas activas que coincidan con el pedido. Se prioriza el ámbito de productos más específico: categoría, subfamilia, familia y todos los productos.", "Active rules matching the order are checked. The most specific product scope takes priority: category, subfamily, family, then all products.")}</li>
          <li>{t("Una asignación habitual también puede exigir aprobación cuando no hay una regla de envío coincidente.", "A standard approver assignment can also require approval when no sender rule matches.")}</li>
          <li>{t("Cuando una regla exige aprobación, primero se buscan asignaciones habituales activas que coincidan con solicitante, productos e importe.", "When a rule requires approval, active standard assignments are matched by requester, products and amount first.")}</li>
          <li>{t("Si esa regla requiere aprobación y no coincide ninguna asignación habitual, se buscan suplentes con los mismos filtros.", "If that rule requires approval and no standard assignment matches, fallback assignments are checked using the same filters.")}</li>
          <li>{t("No se puede activar una regla que requiera aprobación sin cobertura completa de aprobadores.", "A rule requiring approval cannot be activated without complete approver coverage.")}</li>
        </ol></section>
        <section><h3>{t("Qué significa cada campo", "What each field means")}</h3><dl>
          <div><dt>{t("Aprobador", "Approver")}</dt><dd>{t("La persona seleccionada arriba: decide sobre el pedido.", "The person selected at the top: they decide on the order.")}</dd></div>
          <div><dt>{t("Solicitante", "Requester")}</dt><dd>{t("Elige una persona, el origen automático de cobertura de stock o todos los solicitantes. Las reglas del origen automático tienen prioridad sobre las generales cuando coinciden productos e importe.", "Choose a person, the automatic par stock source or all requesters. Matching automatic source rules take priority over general assignments.")}</dd></div>
          <div><dt>{t("Nivel", "Level")}</dt><dd>{t("Las aprobaciones avanzan en orden: primero, segundo, tercero y último.", "Approvals run in order: first, second, third and last.")}</dd></div>
          <div><dt>{t("Decisión", "Decision")}</dt><dd>{t("«Un aprobador» requiere una decisión de ese nivel. «Todos» requiere la de cada aprobador asignado.", "“Any one” needs one decision at that level. “All” needs every assigned approver.")}</dd></div>
          <div><dt>{t("Productos e importe", "Products and amount")}</dt><dd>{t("Limitan qué pedidos cubre la asignación. Los límites vacíos permiten cualquier importe.", "Limit which orders the assignment covers. Blank limits allow any amount.")}</dd></div>
          <div><dt>{t("Suplente", "Fallback")}</dt><dd>{t("Se usa cuando no coincide ninguna asignación habitual; los demás filtros siguen aplicándose.", "Used only when no standard assignment matches; all other filters still apply.")}</dd></div>
        </dl></section>
        <section className="poep-helpExample"><h3>{t("Ejemplo · Un suplente para todos los pedidos", "Example · A fallback for all orders")}</h3><p>{t("Todos los usuarios · Primer nivel · Un aprobador · Todos los productos · Importes sin límites · Tipo suplente · Asignación activa.", "All users · First level · Any one · All products · No amount limits · Fallback type · Active assignment.")}</p></section>
      </POApprovalDialog>

      <POApprovalDialog isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} busy={saving} width="narrow" className="poep-confirm" title={deleteTarget?.target === "sender" ? t("Eliminar regla de envío", "Delete sender rule") : t("Eliminar asignación de aprobador", "Delete approver assignment")} footer={<><button type="button" className="poep-button" disabled={saving} onClick={() => setDeleteTarget(null)}>{t("Cancelar", "Cancel")}</button><button type="button" className="poep-button poep-button--danger" disabled={saving} onClick={() => deleteTarget?.target === "sender" ? deleteSenderRule(deleteTarget.rule) : deleteApproverRule(deleteTarget.rule)}>{saving ? t("Eliminando…", "Deleting…") : t("Eliminar", "Delete")}</button></>}>
        <p>{t("Esta regla dejará de aplicarse a los pedidos. Comprueba que los permisos y la cobertura de aprobación siguen siendo los deseados.", "This rule will no longer apply to orders. Check that sending permissions and approver coverage remain as intended.")}</p>
        {deleteTarget ? <div className="poep-deleteSummary"><strong>{userLabel(selectedUser)}</strong><span>{getScopeText(deleteTarget.rule)}</span><span>{amountText(deleteTarget.rule)}</span></div> : null}
      </POApprovalDialog>
    </>
  );
}
